import React, { useState, useRef } from 'react';
import axiosInstance from '../../../api/axiosConfig';
import {
  HiOutlinePaperClip, HiOutlineTrash, HiOutlineDownload,
  HiOutlineUpload, HiOutlinePencil, HiOutlineX, HiOutlineCheck
} from 'react-icons/hi';

const formatBytes = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const extIcon = (name = '') => {
  const ext = name.split('.').pop()?.toLowerCase();
  if (['pdf'].includes(ext)) return '📄';
  if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return '🖼️';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return '📊';
  if (['doc', 'docx'].includes(ext)) return '📝';
  return '📎';
};

// Browser navigation (target=_blank) doesn't carry the JWT (it's in memory, not a cookie),
// so the /api/files/* endpoint would 401. Fetch the file as a blob via axios (which injects
// the token), then either open it in a new tab (PDFs / images) or trigger a download.
const openProtectedFile = async (fileUrl, originalName) => {
  try {
    const path = fileUrl.startsWith('http') ? new URL(fileUrl).pathname : fileUrl;
    const response = await axiosInstance.get(path, { responseType: 'blob' });
    const blobType = response.data.type || 'application/octet-stream';
    const blob = new Blob([response.data], { type: blobType });
    const url = URL.createObjectURL(blob);
    if (blobType === 'application/pdf' || blobType.startsWith('image/')) {
      window.open(url, '_blank');
    } else {
      const a = document.createElement('a');
      a.href = url;
      a.download = originalName || 'download';
      a.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  } catch (err) {
    console.error(err);
    alert(err.response?.data?.message || 'Failed to open attachment');
  }
};

/**
 * Reusable attachments UI for Strategic Vision entities.
 *
 * Props:
 *  - entityType: 'missions' | 'goals' | 'action-items' | 'tasks' | 'meeting-minutes'
 *  - entityId:   the parent _id
 *  - attachments: array of { _id, fileName, originalName, fileUrl, fileType, fileSize, description, uploadedBy, uploadedAt }
 *  - canEdit:    boolean — controls upload / edit / delete buttons
 *  - onChange:   called after add or delete with the fresh attachments[]
 *  - compact:    smaller spacing
 */
const AttachmentList = ({
  entityType, entityId, attachments = [], canEdit = false,
  onChange, compact = false
}) => {
  // Pending uploads: files the user picked but hasn't sent yet. Each has a description input.
  const [pending, setPending] = useState([]); // [{ file, description }]
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  // Per-attachment description edit state, keyed by attachment._id
  const [editingDesc, setEditingDesc] = useState({});  // { [attId]: true }
  const [descDraft,   setDescDraft]   = useState({});  // { [attId]: string }

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (files.length > 5) {
      alert('You can upload up to 5 files at a time.');
      e.target.value = '';
      return;
    }
    setPending(files.map(f => ({ file: f, description: '' })));
    e.target.value = '';
  };

  const updatePendingDesc = (idx, value) => {
    setPending(prev => prev.map((p, i) => i === idx ? { ...p, description: value } : p));
  };

  const removePending = (idx) => {
    setPending(prev => prev.filter((_, i) => i !== idx));
  };

  const cancelPending = () => setPending([]);

  const submitPending = async () => {
    if (pending.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      pending.forEach(p => {
        fd.append('files', p.file);
        fd.append('descriptions', p.description || '');
      });
      const { data } = await axiosInstance.post(
        `/api/vision/${entityType}/${entityId}/attachments`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      onChange?.(data.data);
      setPending([]);
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const remove = async (attId) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await axiosInstance.delete(`/api/vision/${entityType}/${entityId}/attachments/${attId}`);
      onChange?.(attachments.filter(a => a._id !== attId));
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || 'Delete failed');
    }
  };

  const startEditDesc = (att) => {
    setEditingDesc(prev => ({ ...prev, [att._id]: true }));
    setDescDraft(prev => ({ ...prev, [att._id]: att.description || '' }));
  };
  const cancelEditDesc = (attId) => {
    setEditingDesc(prev => { const n = { ...prev }; delete n[attId]; return n; });
    setDescDraft(prev => { const n = { ...prev }; delete n[attId]; return n; });
  };
  const saveEditDesc = async (attId) => {
    const description = (descDraft[attId] || '').trim();
    try {
      const { data } = await axiosInstance.patch(
        `/api/vision/${entityType}/${entityId}/attachments/${attId}`,
        { description }
      );
      onChange?.(data.data);
      cancelEditDesc(attId);
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || 'Failed to update description');
    }
  };

  const fontSize = compact ? 11 : 12;
  const padding = compact ? '4px 6px' : '6px 8px';

  return (
    <div style={{ marginTop: compact ? 6 : 10 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6,
        fontSize: 10, fontWeight: 700, color: '#64748b',
        textTransform: 'uppercase', letterSpacing: 0.4
      }}>
        <HiOutlinePaperClip size={12} />
        Attachments ({attachments.length})
        {canEdit && pending.length === 0 && (
          <>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
              onChange={onPick}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              style={{
                marginLeft: 'auto', background: '#a8336b', color: '#fff',
                border: 'none', borderRadius: 4, fontSize: 10, padding: '3px 8px',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600
              }}
            >
              <HiOutlineUpload size={11} />
              Choose files
            </button>
          </>
        )}
      </div>

      {/* Pending-upload tray: one row per picked file with a description field */}
      {pending.length > 0 && (
        <div style={{
          background: '#fffbeb', border: '1px dashed #fbbf24',
          borderRadius: 6, padding: 8, marginBottom: 8
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#92400e', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Add description for each file
          </div>
          {pending.map((p, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 13 }}>{extIcon(p.file.name)}</span>
              <span style={{ fontSize, fontWeight: 500, color: '#334155', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '0 1 200px' }} title={p.file.name}>
                {p.file.name}
              </span>
              <span style={{ fontSize: fontSize - 1, color: '#94a3b8' }}>{formatBytes(p.file.size)}</span>
              <input
                type="text"
                value={p.description}
                onChange={e => updatePendingDesc(idx, e.target.value)}
                placeholder="What is this document about?"
                style={{ flex: 1, fontSize, padding: '4px 8px', border: '1px solid #fde68a', borderRadius: 4, background: '#fff' }}
              />
              <button type="button" onClick={() => removePending(idx)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 2 }}>
                <HiOutlineX size={12} />
              </button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 4 }}>
            <button type="button" onClick={cancelPending} disabled={uploading}
              style={{ background: '#fff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 11, padding: '4px 10px', cursor: 'pointer' }}>
              Cancel
            </button>
            <button type="button" onClick={submitPending} disabled={uploading}
              style={{ background: '#a8336b', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, padding: '4px 12px', cursor: uploading ? 'wait' : 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
              <HiOutlineUpload size={11} />
              {uploading ? 'Uploading…' : `Upload ${pending.length}`}
            </button>
          </div>
        </div>
      )}

      {attachments.length === 0 && pending.length === 0 && (
        <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
          No attachments yet.
        </div>
      )}

      {attachments.map(att => {
        const isEditing = !!editingDesc[att._id];
        return (
          <div
            key={att._id}
            style={{
              padding, background: '#fff', border: '1px solid #e2e8f0',
              borderRadius: 6, marginBottom: 4, fontSize
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>{extIcon(att.originalName)}</span>
              <button
                type="button"
                onClick={() => openProtectedFile(att.fileUrl, att.originalName)}
                style={{
                  flex: 1, color: '#1e40af', textDecoration: 'none', fontWeight: 500,
                  minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0, fontSize: 'inherit'
                }}
                title={att.originalName}
              >
                {att.originalName}
              </button>
              <span style={{ color: '#94a3b8', fontSize: fontSize - 1 }}>
                {formatBytes(att.fileSize)}
              </span>
              {att.uploadedAt && (
                <span style={{ color: '#94a3b8', fontSize: fontSize - 1 }}>
                  {new Date(att.uploadedAt).toLocaleDateString('en-GB')}
                </span>
              )}
              <button
                type="button"
                onClick={() => openProtectedFile(att.fileUrl, att.originalName)}
                title="Open / download"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 2, display: 'flex' }}
              >
                <HiOutlineDownload size={13} />
              </button>
              {canEdit && !isEditing && (
                <button
                  type="button"
                  onClick={() => startEditDesc(att)}
                  title="Edit description"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 2, display: 'flex' }}
                >
                  <HiOutlinePencil size={12} />
                </button>
              )}
              {canEdit && (
                <button
                  type="button"
                  onClick={() => remove(att._id)}
                  title="Delete"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 2 }}
                >
                  <HiOutlineTrash size={12} />
                </button>
              )}
            </div>

            {/* Description row */}
            {isEditing ? (
              <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }}>
                <input
                  type="text"
                  autoFocus
                  value={descDraft[att._id] || ''}
                  onChange={e => setDescDraft(prev => ({ ...prev, [att._id]: e.target.value }))}
                  onKeyDown={e => {
                    if (e.key === 'Enter') saveEditDesc(att._id);
                    else if (e.key === 'Escape') cancelEditDesc(att._id);
                  }}
                  placeholder="What is this document about?"
                  style={{ flex: 1, fontSize, padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: 4 }}
                />
                <button type="button" onClick={() => saveEditDesc(att._id)} title="Save"
                  style={{ background: '#10b981', color: '#fff', border: 'none', padding: '3px 8px', borderRadius: 4, fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2 }}>
                  <HiOutlineCheck size={11} />
                </button>
                <button type="button" onClick={() => cancelEditDesc(att._id)} title="Cancel"
                  style={{ background: 'transparent', border: '1px solid #cbd5e1', padding: '3px 8px', borderRadius: 4, fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2 }}>
                  <HiOutlineX size={11} />
                </button>
              </div>
            ) : att.description ? (
              <div style={{ marginTop: 4, marginLeft: 22, fontSize: fontSize - 1, color: '#64748b', fontStyle: 'italic' }}>
                {att.description}
              </div>
            ) : canEdit ? (
              <div style={{ marginTop: 4, marginLeft: 22 }}>
                <button type="button" onClick={() => startEditDesc(att)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: fontSize - 1, padding: 0, fontStyle: 'italic', textDecoration: 'underline dotted' }}>
                  + Add description
                </button>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export default AttachmentList;
